// PIN profile administration. Pick a station, see assigned PINs,
// add/disable/reset.

import { useEffect, useState } from 'react';
import { api } from '../../api';
import Modal from '../../components/Modal';

export default function PinAdminPage() {
  const [stations, setStations] = useState([]);
  const [stationId, setStationId] = useState(null);
  const [pins, setPins] = useState([]);
  const [eligible, setEligible] = useState([]);
  const [err, setErr] = useState('');
  const [adding, setAdding] = useState(false);
  const [resetting, setResetting] = useState(null);

  useEffect(() => {
    api.listStations().then(rows => {
      setStations(rows);
      if (rows[0]) setStationId(rows[0].id);
    }).catch(e => setErr(e.message));
  }, []);

  function reloadPins(sid) {
    api.listPinsForStation(sid).then(setPins).catch(e => setErr(e.message));
    api.listEligibleForPin(sid).then(setEligible).catch(() => {});
  }
  useEffect(() => { if (stationId) reloadPins(stationId); }, [stationId]);

  async function toggleActive(p) {
    try { await api.updatePin(p.id, { is_active: p.is_active ? 0 : 1 }); reloadPins(stationId); }
    catch (e) { setErr(e.message); }
  }

  return (
    <div className="page">
      <div className="page-header">
        <h2>Dispatcher PINs</h2>
        <div className="filter-bar">
          <select value={stationId || ''} onChange={e => setStationId(parseInt(e.target.value, 10))}>
            {stations.map(s => (
              <option key={s.id} value={s.id}>
                {s.full_name}{s.station_region ? ` — ${s.station_region}` : ''}
              </option>
            ))}
          </select>
          <button className="btn-primary" onClick={() => setAdding(true)} disabled={!stationId}>+ Add Dispatcher</button>
        </div>
      </div>
      {stations.length === 0 && (
        <div className="empty-state">
          No station accounts found. Mark a user as a station account (set <code>is_station_account=1</code> and a <code>station_region</code>) in the users table to get started.
        </div>
      )}
      {err && <div className="error-banner">{err}</div>}

      <table className="data-table">
        <thead>
          <tr><th>Name</th><th>Permission</th><th>Active</th><th>PTO Balance</th><th>Last PIN Change</th><th></th></tr>
        </thead>
        <tbody>
          {pins.map(p => (
            <tr key={p.id}>
              <td>{p.full_name}</td>
              <td>{p.permission_level}</td>
              <td>{p.is_active ? '✓' : '—'}</td>
              <td>{(p.pto_balance_hours || 0).toFixed(2)}h</td>
              <td className="muted">{p.pin_last_changed_at || '—'}</td>
              <td>
                <button className="btn-secondary" onClick={() => setResetting(p)}>Reset PIN</button>
                <button className="btn-secondary" onClick={() => toggleActive(p)}>{p.is_active ? 'Disable' : 'Enable'}</button>
              </td>
            </tr>
          ))}
          {pins.length === 0 && <tr><td colSpan="6" className="empty">No PINs assigned at this station.</td></tr>}
        </tbody>
      </table>

      {adding && (
        <AddPinModal stationId={stationId} eligible={eligible} onClose={() => setAdding(false)} onDone={() => { setAdding(false); reloadPins(stationId); }} />
      )}
      {resetting && (
        <ResetPinModal pin={resetting} onClose={() => setResetting(null)} onDone={() => { setResetting(null); reloadPins(stationId); }} />
      )}
    </div>
  );
}

function AddPinModal({ stationId, eligible, onClose, onDone }) {
  const [userId, setUserId] = useState(eligible[0]?.id || '');
  const [pin, setPin] = useState('');
  const [permission, setPermission] = useState('dispatcher');
  const [err, setErr] = useState('');

  async function submit() {
    if (!userId || !/^\d{4,6}$/.test(pin)) { setErr('Pick a user and enter a 4–6 digit PIN'); return; }
    try {
      await api.createPin({ user_id: userId, home_station_user_id: stationId, pin, permission_level: permission });
      onDone();
    } catch (e) { setErr(e.message); }
  }

  return (
    <Modal title="Add Dispatcher PIN" onClose={onClose}>
      {err && <div className="form-error">{err}</div>}
      <div className="form-grid">
        <label>Dispatcher
          <select value={userId} onChange={e => setUserId(parseInt(e.target.value, 10))}>
            <option value="">— Select user —</option>
            {eligible.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
          </select>
        </label>
        <label>PIN (4–6 digits)
          <input type="text" inputMode="numeric" pattern="\d{4,6}" value={pin} onChange={e => setPin(e.target.value.replace(/\D/g,''))} maxLength="6" />
        </label>
        <label>Permission
          <select value={permission} onChange={e => setPermission(e.target.value)}>
            <option value="dispatcher">Dispatcher</option>
            <option value="manager">Manager</option>
          </select>
        </label>
      </div>
      <div className="form-actions">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" onClick={submit}>Create</button>
      </div>
    </Modal>
  );
}

function ResetPinModal({ pin, onClose, onDone }) {
  const [val, setVal] = useState('');
  const [err, setErr] = useState('');
  async function submit() {
    if (!/^\d{4,6}$/.test(val)) { setErr('PIN must be 4–6 digits'); return; }
    try { await api.resetPin(pin.id, val); onDone(); } catch (e) { setErr(e.message); }
  }
  return (
    <Modal title={`Reset PIN — ${pin.full_name}`} onClose={onClose}>
      {err && <div className="form-error">{err}</div>}
      <label>New PIN (4–6 digits)
        <input type="text" inputMode="numeric" pattern="\d{4,6}" value={val} onChange={e => setVal(e.target.value.replace(/\D/g,''))} maxLength="6" />
      </label>
      <div className="form-actions">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" onClick={submit}>Reset</button>
      </div>
    </Modal>
  );
}
